// ─────────────────────────────────────────────
//  InvestmentSummary.jsx
//  Resultados: ganancia neta y capital final
// ─────────────────────────────────────────────

import React from 'react';
import styles from './InvestmentSummary.module.css';
import { formatCurrency } from '../../utils/format';
import { formatTerm } from './TermPicker';

export default function InvestmentSummary({
	capital,
	grossGain,
	isrAmount,
	netGain,
	netFinal,
	returnPct,
	ratePct,
	effectiveRate,
	comision,
	parsedIsr,
	termPos,
	hasCapital,
	isCompound,
}) {
	const fmt    = (v) => (hasCapital ? formatCurrency(v) : '—');
	const fmtAmt = (v) => (hasCapital ? formatCurrency(v).replace('$ ', '') : '—');

	const hasComision = comision > 0;
	const hasIsr      = parsedIsr > 0;

	return (
		<section className={styles.card} aria-label="Resultados de inversión">

			{/* ─── Parámetros ─── */}
			<div className={styles.row}>
				<span className={styles.rowLabel}>Capital inicial</span>
				<span className={styles.rowValue}>{fmt(capital)}</span>
			</div>

			<div className={styles.row}>
				<span className={styles.rowLabel}>
					Tasa bruta
					<span className={styles.tag}> {ratePct}%</span>
				</span>
				{hasComision && (
					<span className={styles.deductTag}>
						− {comision}% comisión = <strong>{effectiveRate.toFixed(1)}%</strong> neta
					</span>
				)}
			</div>

			<div className={styles.row}>
				<span className={styles.rowLabel}>Plazo</span>
				<span className={styles.rowValue}>
					{formatTerm(termPos)}{' '}
					<span className={styles.tag}>
						{isCompound ? '· comp. mensual' : '· simple'}
					</span>
				</span>
			</div>

			<div className={styles.divider} />

			{/* ─── Ganancia bruta ─── */}
			{hasIsr ? (
				<>
					<div className={styles.row}>
						<span className={styles.subLabel}>Ganancia bruta</span>
						<span className={styles.subValue}>
							{hasCapital ? `+ ${fmtAmt(grossGain)}` : '—'}
						</span>
					</div>
					<div className={styles.row}>
						<span className={styles.subLabel}>
							ISR estimado <span className={styles.tag}>({parsedIsr}% de ganancia)</span>
						</span>
						<span className={styles.isrValue}>
							{hasCapital ? `− ${fmtAmt(isrAmount)}` : '—'}
						</span>
					</div>
					<div className={styles.dividerLight} />
				</>
			) : null}

			{/* ─── Ganancia neta ─── */}
			<div className={styles.row}>
				<span className={styles.gainLabel}>
					Ganancia {hasIsr ? 'neta' : 'estimada'}
				</span>
				<span className={styles.gainValue}>
					{hasCapital ? `+ ${fmtAmt(netGain)}` : '—'}
				</span>
			</div>

			<div className={styles.divider} />

			{/* ─── Capital final ─── */}
			<div className={`${styles.row} ${styles.totalRow}`}>
				<span className={styles.totalLabel}>Capital final</span>
				<span className={styles.totalValue}>{fmt(netFinal)}</span>
			</div>

			{/* ─── Rendimiento badge ─── */}
			{hasCapital && (
				<div className={styles.returnBadge} aria-live="polite">
					<span className={styles.returnText}>
						Rendimiento total{' '}
						<strong className={styles.returnPct}>
							+{returnPct.toFixed(2).replace(/\.00$/, '')}%
						</strong>
					</span>
				</div>
			)}

		</section>
	);
}
